import ApiService from '@/util/api.service';

const LS_MIC_KEY = 'radioCallBar.deviceMic';
const LS_SPEAKER_KEY = 'radioCallBar.deviceSpeaker';
// Mismo base path que ApiService: el DELETE de pagehide va por fetch nativo
// (axios no soporta keepalive) y debe pegarle al mismo endpoint.
const API_BASE = import.meta.env.VITE_APP_BASE_API || '/api';

async function fetchIceConfig() {
  try {
    return (await ApiService({ url: '/v2/radio/calls/webrtc/ice-config', method: 'get' })) || {};
  } catch (err) {
    // Degradar a config vacía (sin STUN/TURN): las llamadas dentro de la misma
    // red funcionan, pero el NAT traversal entre redes puede fallar. Logueamos
    // para que NO sea un fallo silencioso al diagnosticar "audio que no llega".
    console.warn('[radio] no se pudo obtener ICE config; NAT traversal puede fallar:', err && err.message);
    return {};
  }
}

function waitIceComplete(pc) {
  if (pc.iceGatheringState === 'complete') return Promise.resolve();
  return new Promise((res) => {
    const t = setTimeout(res, 3000);
    const onChange = () => {
      if (pc.iceGatheringState === 'complete') {
        clearTimeout(t);
        pc.removeEventListener('icegatheringstatechange', onChange);
        res();
      }
    };
    pc.addEventListener('icegatheringstatechange', onChange);
  });
}

class RadioAudioBridge {
  constructor() {
    this.pc = null;
    this.localStream = null;
    this.remoteAudioEl = null;
    this.callId = null;
    // NUEVO: WebAudio para los dos VU meters (local + remoto)
    this.audioCtx = null;
    this.analyser = null; // mic local
    this.analyserSource = null;
    this.remoteAnalyser = null; // audio remoto del radio
    this.remoteAnalyserSource = null;
    this._pagehideHandler = null;
    // Marca si el POST del offer ya se envió: a partir de ahí el bridge puede
    // existir en el backend y un open() que falle debe emitir el DELETE.
    this._offerSent = false;
  }

  // DELETE del audio leg por fetch nativo (axios/ApiService no soporta keepalive).
  // keepalive=true para el camino de pagehide (la página muere); false cuando la
  // página sigue viva (cleanup de un open fallido). callId capturado, no this.callId
  // (que se anula al fallar el open). Best-effort: los errores se ignoran.
  _deleteAudioLeg(cid, keepalive) {
    if (!cid) return;
    try {
      fetch(`${API_BASE}/v2/radio/calls/${cid}/audio`, {
        method: 'DELETE',
        keepalive: !!keepalive,
        credentials: 'include',
      }).catch(() => {});
    } catch (_) { /* ignore */ }
  }

  /**
   * Pide permiso de mic sin abrir la llamada. Retorna true si lo tenemos.
   * - Si el navegador soporta Permissions API y dice 'granted', no abre prompt.
   * - Si dice 'prompt', hace getUserMedia con stream temporal y lo libera.
   * - Si dice 'denied' o getUserMedia falla, retorna false.
   */
  async requestMicPermission() {
    try {
      if (navigator.permissions && navigator.permissions.query) {
        try {
          const status = await navigator.permissions.query({ name: 'microphone' });
          if (status.state === 'granted') return true;
          if (status.state === 'denied') return false;
        } catch (_) { /* algunos navegadores no soportan 'microphone' como permission name */ }
      }
      const tmp = await navigator.mediaDevices.getUserMedia({ audio: true });
      tmp.getTracks().forEach((t) => t.stop());
      return true;
    } catch (_) {
      return false;
    }
  }

  /**
   * Lista dispositivos disponibles separados por kind.
   * Retorna { mics: [{deviceId, label}], speakers: [{deviceId, label}] }.
   * El `label` puede venir vacío si el navegador todavía no concedió permiso.
   */
  async listDevices() {
    try {
      const all = await navigator.mediaDevices.enumerateDevices();
      const mics = all
        .filter((d) => d.kind === 'audioinput')
        .map((d) => ({ deviceId: d.deviceId, label: d.label || 'Micrófono sin nombre' }));
      const speakers = all
        .filter((d) => d.kind === 'audiooutput')
        .map((d) => ({ deviceId: d.deviceId, label: d.label || 'Altavoz sin nombre' }));
      return { mics, speakers };
    } catch (_) {
      return { mics: [], speakers: [] };
    }
  }

  async open(callId, type = 'mcx') {
    if (this.pc) await this.close(this.callId);
    this.callId = callId;
    this._offerSent = false;

    // Todo el setup va dentro del try: si cualquier paso (getUserMedia, SDP,
    // POST al backend, setRemoteDescription) falla, dejamos recursos a medio
    // abrir (stream/pc/audioCtx/<audio>). Sin el cleanup, tras ~5-10 intentos
    // fallidos el mic quedaba inutilizable y el pc colgado. close() es
    // idempotente y libera todo antes de propagar el error al caller.
    try {
      const savedMic = window.localStorage.getItem(LS_MIC_KEY);
      if (savedMic) {
        try {
          this.localStream = await navigator.mediaDevices
            .getUserMedia({ audio: { deviceId: { exact: savedMic } } });
        } catch (micErr) {
          // El mic guardado ya no está disponible (desconectado, permiso
          // revocado, etc.). Degradar al mic por defecto en vez de fallar la
          // llamada entera. La preferencia se conserva por si el device vuelve.
          console.warn('[radio] mic guardado no disponible, usando el default:', micErr && micErr.name);
          this.localStream = await navigator.mediaDevices.getUserMedia({ audio: true });
        }
      } else {
        this.localStream = await navigator.mediaDevices.getUserMedia({ audio: true });
      }

      // VU meter local: analyser conectado al stream del mic.
      try {
        const AC = window.AudioContext || window.webkitAudioContext;
        this.audioCtx = new AC();
        this.analyser = this.audioCtx.createAnalyser();
        this.analyser.fftSize = 256;
        this.analyser.smoothingTimeConstant = 0.5;
        this.analyserSource = this.audioCtx.createMediaStreamSource(this.localStream);
        this.analyserSource.connect(this.analyser);
      } catch (_) {
        this.audioCtx = null;
        this.analyser = null;
        this.analyserSource = null;
      }

      const iceCfg = await fetchIceConfig();
      this.pc = new RTCPeerConnection({
        iceServers: iceCfg.iceServers || [],
        ...(iceCfg.iceTransportPolicy ? { iceTransportPolicy: iceCfg.iceTransportPolicy } : {}),
      });

      // IMPORTANTE: pasar el MediaStreamTrack directo al addTransceiver para que
      // el sender quede atado al mic. Si solo pasas 'audio' (string), el sender
      // queda sin track y packetsSent se queda en 0 (no manda nada al peer).
      // `streams` aquí es solo para asociación informativa del receiver.
      const audioTrack = this.localStream.getAudioTracks()[0];
      const tx = this.pc.addTransceiver(audioTrack, {
        direction: 'sendrecv',
        streams: [this.localStream],
      });
      try {
        const caps = window.RTCRtpSender && RTCRtpSender.getCapabilities
          ? RTCRtpSender.getCapabilities('audio')
          : null;
        if (caps && tx.setCodecPreferences) {
          const pcma = caps.codecs.find((c) => c.mimeType.toLowerCase() === 'audio/pcma');
          if (pcma) tx.setCodecPreferences([pcma, ...caps.codecs.filter((c) => c !== pcma)]);
        }
      } catch (_) { /* navegador no soporta setCodecPreferences */ }

      // Mute mic hasta que el floor sea Granted
      this.localStream.getAudioTracks().forEach((t) => { t.enabled = false; });

      this.remoteAudioEl = document.createElement('audio');
      this.remoteAudioEl.autoplay = true;
      this.remoteAudioEl.style.display = 'none';
      const savedSpeaker = window.localStorage.getItem(LS_SPEAKER_KEY);
      if (savedSpeaker && this.remoteAudioEl.setSinkId) {
        try { await this.remoteAudioEl.setSinkId(savedSpeaker); } catch (_) { /* ignore */ }
      }
      document.body.appendChild(this.remoteAudioEl);

      this.pc.ontrack = (e) => {
        const [stream] = e.streams;
        if (!stream) return;
        this.remoteAudioEl.srcObject = stream;
        // VU meter remoto: analyser sobre el stream entrante del radio
        try {
          if (this.audioCtx && !this.remoteAnalyser) {
            this.remoteAnalyser = this.audioCtx.createAnalyser();
            this.remoteAnalyser.fftSize = 256;
            this.remoteAnalyser.smoothingTimeConstant = 0.5;
            this.remoteAnalyserSource = this.audioCtx.createMediaStreamSource(stream);
            this.remoteAnalyserSource.connect(this.remoteAnalyser);
          }
        } catch (_) {
          this.remoteAnalyser = null;
          this.remoteAnalyserSource = null;
        }
      };

      const offer = await this.pc.createOffer();
      await this.pc.setLocalDescription(offer);
      await waitIceComplete(this.pc);

      // Registrar el pagehide ANTES del POST: el bridge existe en el backend desde
      // que el POST se procesa, así que un reload/cierre a partir de aquí debe
      // entregar el DELETE (antes se registraba tras el éxito → la ventana del
      // offer en vuelo quedaba desprotegida). Captura callId porque this.callId se
      // anula si el open falla.
      this._pagehideHandler = () => this._deleteAudioLeg(callId, true);
      window.addEventListener('pagehide', this._pagehideHandler);
      this._offerSent = true;

      const { sdp: answerSdp } = await ApiService({
        url: `/v2/radio/calls/${callId}/audio/offer`,
        method: 'post',
        params: { type },
        data: { sdp: this.pc.localDescription.sdp },
      });
      await this.pc.setRemoteDescription({ type: 'answer', sdp: answerSdp });
    } catch (err) {
      // Si el POST del offer ya salió, el backend pudo crear el bridge: emitir el
      // DELETE explícito (página viva, sin keepalive) para no dejarlo colgado — no
      // basta con confiar en que el operador cuelgue (un reload lo fugaría hasta
      // que el reaper del backend lo coseche). Si el POST no salió, no hay bridge.
      if (this._offerSent) this._deleteAudioLeg(callId, false);
      this._releaseLocal();
      this.callId = null;
      throw err;
    }
  }

  setMicEnabled(enabled) {
    if (!this.localStream) return;
    this.localStream.getAudioTracks().forEach((t) => { t.enabled = enabled; });
  }

  getAnalyser() {
    return this.analyser;
  }

  getRemoteAnalyser() {
    return this.remoteAnalyser;
  }

  /**
   * Cambia el micrófono en caliente. Hace replaceTrack en el sender del PC,
   * sustituye this.localStream y reconecta el analyser del VU.
   * Serializa llamadas concurrentes con _micSwapping para evitar interleaving.
   */
  async setMicDevice(deviceId) {
    if (!this.pc) {
      // Sin llamada activa no hay stream que reemplazar, pero persistimos la
      // preferencia: open() la aplica vía savedMic en el próximo getUserMedia.
      try { window.localStorage.setItem(LS_MIC_KEY, deviceId); } catch (_) { /* ignore */ }
      return;
    }
    if (this._micSwapping) return;
    this._micSwapping = true;
    try {
      let newStream;
      try {
        newStream = await navigator.mediaDevices.getUserMedia({
          audio: { deviceId: { exact: deviceId } },
        });
      } catch (err) {
        console.warn('setMicDevice failed', err);
        return;
      }
      const newTrack = newStream.getAudioTracks()[0];

      // Preservar el enabled state ANTES de replaceTrack para evitar ventana de unmute
      const prevTrack = this.localStream && this.localStream.getAudioTracks()[0];
      const prevEnabled = prevTrack ? prevTrack.enabled : false;
      newTrack.enabled = prevEnabled;

      const sender = this.pc.getSenders().find((s) => s.track && s.track.kind === 'audio')
        || this.pc.getSenders()[0];
      if (sender && sender.replaceTrack) await sender.replaceTrack(newTrack);

      // Soltar el stream anterior
      try {
        if (this.localStream) this.localStream.getTracks().forEach((t) => t.stop());
      } catch (_) { /* ignore */ }
      this.localStream = newStream;

      // Reconectar analyser al nuevo stream
      if (this.audioCtx && this.analyser) {
        try {
          if (this.analyserSource) this.analyserSource.disconnect();
        } catch (_) { /* ignore */ }
        this.analyserSource = this.audioCtx.createMediaStreamSource(this.localStream);
        this.analyserSource.connect(this.analyser);
      }

      window.localStorage.setItem(LS_MIC_KEY, deviceId);
    } finally {
      this._micSwapping = false;
    }
  }

  /**
   * Cambia el altavoz al que se reproduce el audio remoto.
   * En Firefox y otros sin setSinkId el método retorna false sin error.
   */
  async setSpeakerDevice(deviceId) {
    const supported = typeof HTMLMediaElement !== 'undefined'
      && typeof HTMLMediaElement.prototype.setSinkId === 'function';
    if (!supported) return false;
    try {
      // Si hay llamada activa, aplicarlo de inmediato al <audio> remoto. Si no,
      // solo persistir: open() lo reaplica vía savedSpeaker en la próxima llamada.
      if (this.remoteAudioEl && this.remoteAudioEl.setSinkId) {
        await this.remoteAudioEl.setSinkId(deviceId);
      }
      window.localStorage.setItem(LS_SPEAKER_KEY, deviceId);
      return true;
    } catch (err) {
      console.warn('setSpeakerDevice failed', err);
      return false;
    }
  }

  // Libera todos los recursos LOCALES (analysers, AudioContext, mic stream,
  // peer connection, <audio> remoto, listener de pagehide). Reusado por close()
  // y por el cleanup de error de open(). El DELETE del backend lo maneja el
  // caller (close() vía ApiService; el catch de open() vía _deleteAudioLeg si el
  // offer ya se había enviado) — este método NO toca el backend.
  _releaseLocal() {
    if (this._pagehideHandler) {
      window.removeEventListener('pagehide', this._pagehideHandler);
      this._pagehideHandler = null;
    }
    this._offerSent = false;
    try { if (this.analyserSource) this.analyserSource.disconnect(); } catch (_) { /* ignore */ }
    try { if (this.analyser) this.analyser.disconnect(); } catch (_) { /* ignore */ }
    try { if (this.remoteAnalyserSource) this.remoteAnalyserSource.disconnect(); } catch (_) { /* ignore */ }
    try { if (this.remoteAnalyser) this.remoteAnalyser.disconnect(); } catch (_) { /* ignore */ }
    try { if (this.audioCtx) this.audioCtx.close(); } catch (_) { /* ignore */ }
    this.audioCtx = null;
    this.analyser = null;
    this.analyserSource = null;
    this.remoteAnalyser = null;
    this.remoteAnalyserSource = null;
    try { if (this.localStream) this.localStream.getTracks().forEach((t) => t.stop()); } catch (e) { /* ignore */ }
    try { if (this.pc) this.pc.close(); } catch (e) { /* ignore */ }
    if (this.remoteAudioEl) {
      this.remoteAudioEl.srcObject = null;
      this.remoteAudioEl.remove();
    }
    this.pc = null;
    this.localStream = null;
    this.remoteAudioEl = null;
  }

  async close(callId) {
    if (callId != null && this.callId !== callId) return; // race protect
    const cid = this.callId;
    this._releaseLocal();
    this.callId = null;
    if (cid != null) {
      try {
        await ApiService({ url: `/v2/radio/calls/${cid}/audio`, method: 'delete' });
      } catch (_) { /* fire-and-forget */ }
    }
  }
}

export default new RadioAudioBridge();
